
/* eslint-disable vue/no-mutating-props */
// `target` is the orchestrator's shared form-model object (state.client or a
// guest), passed by reference so this step can record the SOA answers and
// signature. The orchestrator owns it; we mutate its fields in place by design.
import moment from 'moment-timezone'
import { mapGetters } from 'vuex'
import AddressFields from '../parts/AddressFields.vue'
import SoaProductCheckRow from '~/components/checkup/parts/SoaProductCheckRow.vue'

// #605: mirrors the wording the agent scheduler uses (COPY.s4.repRelationshipTip).
const REP_RELATIONSHIP_TIP = 'Examples include Power of Attorney (POA) or legal guardian.'

export default {
    components: { SoaProductCheckRow, AddressFields },
    props: {
        // Eastern-only scheduler: render the appointment time in the zone it was
        // booked in -- never the browser's.
        timezone: { type: String, default: 'America/New_York' },
        target: { type: Object, required: true },
        isPrimary: { type: Boolean, default: true },
        guestPos: { type: Number, default: 0 },
        guestTotal: { type: Number, default: 0 },
        // Appointment meta to echo onto the SOA card
        apptTime: { type: String, default: null },
        apptMethod: { type: String, default: null },
        // #686: collect the signer's full address on this step (returning
        // signers whose session never unlocked the on-file echo).
        needsAddress: { type: Boolean, default: false },
        // Show product-card warnings if user tried to advance
        gate: { type: Boolean, default: false },
    },
    data () { return { signing: false, REP_RELATIONSHIP_TIP } },
    computed: {
        // SOA products now live in the store (soaProducts getter). `products`
        // alias keeps the template + ready() readable.
        ...mapGetters(['soaProducts']),
        products () { return this.soaProducts },
        // The store's products in SoaProductCheckRow's shape.
        checkRows () {
            return this.products.map(p => ({ id: p.value, title: p.title, description: p.description, why: p.more }))
        },
        // The boolean `products` map is the authorization -- what the signed
        // PDF's boxes are filled from -- so it is what "ticked" reads, not
        // product_answers. That keeps an old "unsure" (always authorized)
        // ticked for a client whose wizard state was saved across the deploy.
        anyTicked () { return this.products.some(p => this.isTicked(p.value)) },
        ready () { return this.anyTicked },
        canSign () { return this.ready && !!this.target.soaRole && !!this.signerName && this.repReady },
        isRepresentative () { return this.target.soaRole === 'representative' },
        // A representative signs for someone else — the SOA wants their
        // relationship to the beneficiary alongside their name (#605).
        repReady () {
            if (!this.isRepresentative) return true
            return !!(this.target.soaRepRelationship || '').trim()
        },
        beneficiaryFullName () {
            if (this.isPrimary) {
                return ((this.target.first_name || '') + ' ' + (this.target.last_name || '')).trim()
            }
            return ((this.target.first || '') + ' ' + (this.target.last || '')).trim()
        },
        // Whose name goes into the signature: the beneficiary's own, or the
        // authorized representative's typed name when signing on their behalf.
        signerName () {
            if (this.isRepresentative) {
                return (this.target.soaRepName || '').trim()
            }
            return this.beneficiaryFullName
        },
        firstName () {
            return (this.target.first || this.target.first_name || '').trim() || 'this guest'
        },
        initials () {
            const a = (this.target.first || this.target.first_name || 'A')[0] || 'A'
            const b = (this.target.last || this.target.last_name || '')[0] || ''
            return (a + b).toUpperCase()
        },
        beneficiaryName () {
            return this.beneficiaryFullName || 'Your name'
        },
        // #689 (Aziz): the signed SOA carries the full address, so the card
        // shows it too — the signer's last chance to catch a typo. Empty until
        // a street is on the target (a returning signer before the #686 block,
        // or a guest inheriting the primary's), and then the card is as before.
        beneficiaryAddress () {
            const t = this.target
            if (!(t.street_1 || '').trim()) return ''
            const cityState = [t.city, t.state].filter(Boolean).join(', ')
            const cityStateZip = [cityState, t.zip_code].filter(Boolean).join(' ')
            return [t.street_1, t.street_2, cityStateZip].filter(v => (v || '').trim()).join(', ')
        },
        beneficiaryMeta () {
            const parts = []
            // The ZIP already appears in the address line when there is one.
            if (this.target.zip_code && !this.beneficiaryAddress) parts.push(this.target.zip_code)
            if (this.isPrimary) {
                if (this.target.phone) parts.push(this.target.phone)
            } else if (this.target.relationship) parts.push(this.target.relationship)
            return parts.join(' · ')
        },
        apptLabel () {
            if (this.apptMethod === 'email' || this.apptMethod === 'survey') {
                return 'By email — a licensed advisor will reach out within one business day'
            }
            if (this.apptTime) {
                return moment.tz(this.apptTime, this.timezone).format('dddd, MMMM D [at] h:mm A') + ' · Eastern Time'
            }
            return ''
        },
        apptIcon () {
            return this.apptMethod === 'email' || this.apptMethod === 'survey' ? 'envelope' : 'calendar-check'
        },
        signedAtLabel () {
            if (!this.target.soaSignedAt) return ''
            return moment(this.target.soaSignedAt).format('MMM D, YYYY [at] h:mm A')
        },
    },
    created () { this.prefillProducts() },
    methods: {
        // #689: no Location on this step. Set the ZIP, clear the stale fips;
        // the corrections payload omits an empty fips (StepByStepV2 ~L1395)
        // and the backend re-derives state/fips from the new ZIP on save.
        onAddressZip (zip) {
            this.target.zip_code = zip
            this.target.fips = ''
        },
        isTicked (pid) { return !!(this.target.products || {})[pid] },
        setTick (pid, ticked) {
            const answers = { ...(this.target.product_answers || {}) }
            answers[pid] = ticked ? 'yes' : 'no'
            this.$set(this.target, 'product_answers', answers)
            const products = { ...(this.target.products || {}) }
            products[pid] = !!ticked
            this.$set(this.target, 'products', products)
            // Changing scope after signing voids the signature
            if (this.target.soaSigned) {
                this.target.soaSigned = false
                this.target.soaSignedAt = null
            }
        },
        // Every product starts ticked (legal, 2026-09-24). Only what the client
        // has not answered is filled in, so a return visit keeps their choices,
        // and so does wizard state saved before the deploy (an old "unsure"
        // stays ticked: it was always authorized).
        prefillProducts () {
            const answers = { ...(this.target.product_answers || {}) }
            const products = { ...(this.target.products || {}) }
            let changed = false
            this.products.forEach(({ value: pid }) => {
                if (!answers[pid]) {
                    answers[pid] = 'yes'
                    products[pid] = true
                    changed = true
                } else if (products[pid] === undefined) {
                    products[pid] = answers[pid] === 'yes' || answers[pid] === 'unsure'
                    changed = true
                }
            })
            if (changed) {
                this.$set(this.target, 'product_answers', answers)
                this.$set(this.target, 'products', products)
            }
        },
        setRole (role) {
            this.$set(this.target, 'soaRole', role)
            // Changing who signs after signing voids the signature
            if (this.target.soaSigned) {
                this.target.soaSigned = false
                this.target.soaSignedAt = null
            }
        },
        setRepName (name) {
            this.$set(this.target, 'soaRepName', name)
            if (this.target.soaSigned) {
                this.target.soaSigned = false
                this.target.soaSignedAt = null
            }
        },
        setRepRelationship (value) {
            this.$set(this.target, 'soaRepRelationship', value)
            if (this.target.soaSigned) {
                this.target.soaSigned = false
                this.target.soaSignedAt = null
            }
        },
        insertSignature () {
            if (!this.canSign) return
            this.signing = true
            try {
                // The actual /signed-soas/ POST happens at book time so we
                // have the client_id; here we just mark the wizard state.
                this.target.soaName = this.signerName
                this.target.soaSigned = true
                this.target.soaSignedAt = new Date().toISOString()
                this.$emit('signed')
            } finally {
                this.signing = false
            }
        },
        unsign () {
            this.target.soaSigned = false
            this.target.soaSignedAt = null
        },
    },
}
